// src/views/ApiPortal/ApiPortal.js
import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import { AuthContext } from "../../context/AuthContext";
import lockIcon from "../../assets/lock.png";
import "./ApiPortal.css";

const OPENAPI_URL = "https://esim.globtel.biz/api/openapi.json";

const ADMIN_DENY = new Set([
  // Check Token
  "GET /auth/check-token",

  // Carriers
  "GET /carrier/batch",
  "POST /carrier/",

  // Country
  "PUT /country/{country_id}",

  // External packages
  "GET /external/{package_id}",
  "GET /external/batch",

  // Internal packages
  "DELETE /internal/{package_id}",
  "PUT /internal/{package_id}",
  "POST /internal/",

  // Package link
  "DELETE /link/{link_id}",
  "GET /link/{link_id}",
  "PUT /link/{link_id}",
  "GET /link/internal/{internal_id}",
  "POST /link/",

  // Logs
  "DELETE /log/{log_filename}",
  "GET /log/{log_filename}",

  // Providers
  "DELETE /provider/{provider_id}",
  "GET /provider/{provider_id}",
  "PUT /provider/{provider_id}",
  "GET /provider/batch",
  "POST /provider/",

  // Regions
  "PUT /region/{region_id}",

  // System / utilities
  "GET /user-service-connection",

  // Webhooks - redtea
  "POST /webhook/redtea/new",
  "GET /webhook/redtea",
  "POST /webhook/redtea",

  // Webhooks - telna
  "POST /webhook/telna/dest_url_esim_status_change",
  "POST /webhook/telna/dest_url_package_status_change",
  "POST /webhook/telna/dest_url_package_usage_alert",
  "POST /webhook/telna",

  // Webhooks - transatel
  "DELETE /webhook/transatel/{id}",
  "GET /webhook/transatel/{id}",
  "PUT /webhook/transatel/{webhook_id}",
  "GET /webhook/transatel/list",
  "POST /webhook/transatel/new",
  "POST /webhook/transatel/test/{id}",
  "POST /webhook/transatel",
]);

const ADMIN_PREFIXES = [
  "/external-packages",
  "/package-link",
  "/providers",
  "/stats",
  "/telna",

  "/external",
  "/link",
  "/log",
  "/provider",
  "/webhook",
];

const ADMIN_SUBSTR = ["/admin", "/batch/all", "/batch/admin"];

function resolveRef(spec, ref) {
  if (!ref || !ref.startsWith("#/")) return null;
  const path = ref.slice(2).split("/");
  return path.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
    spec,
  );
}

function isHiddenByExtension(op) {
  return op?.["x-internal"] === true || op?.["x-hidden"] === true;
}

function isAdminSec(op) {
  const sec = op?.security;
  if (!Array.isArray(sec)) return false;
  return sec.some((schemeObj) => {
    const entries = Object.entries(schemeObj || {});
    return entries.some(([schemeName, scopes]) => {
      if (String(schemeName).toLowerCase().includes("admin")) return true;
      if (Array.isArray(scopes)) {
        return scopes.some((s) => String(s).toLowerCase().includes("admin"));
      }
      return false;
    });
  });
}

function EndpointCard({ path, method, op, spec, requiresAuth }) {
  const [open, setOpen] = useState(false);
  const title =
    op.summary || op.operationId || `${method.toUpperCase()} ${path}`;
  const subtitle = op.description || "";

  const params = op.parameters || [];

  function sampleFromSchema(schema, spec, depth = 0) {
    if (!schema) return null;
    const sch = schema.$ref ? resolveRef(spec, schema.$ref) : schema;
    if (!sch) return null;
    if (sch.example !== undefined) return sch.example;
    if (Array.isArray(sch.enum) && sch.enum.length > 0) return sch.enum[0];
    if (Array.isArray(sch.oneOf) && sch.oneOf.length > 0)
      return sampleFromSchema(sch.oneOf[0], spec, depth + 1);
    if (Array.isArray(sch.anyOf) && sch.anyOf.length > 0)
      return sampleFromSchema(sch.anyOf[0], spec, depth + 1);
    if (sch.allOf && Array.isArray(sch.allOf))
      return sampleFromSchema(sch.allOf[0], spec, depth + 1);

    switch (sch.type) {
      case "string":
        if (sch.format === "date-time") return "2025-01-01T00:00:00Z";
        if (sch.format === "date") return "2025-01-01";
        if (sch.format === "uuid")
          return "00000000-0000-0000-0000-000000000000";
        return sch.default ?? "string";
      case "number":
      case "integer":
        return sch.default ?? 0;
      case "boolean":
        return sch.default ?? true;
      case "array":
        return [sampleFromSchema(sch.items, spec, depth + 1)];
      case "object":
      default: {
        const out = {};
        const props = sch.properties || {};
        for (const [k, v] of Object.entries(props)) {
          out[k] = sampleFromSchema(v, spec, depth + 1);
        }
        return out;
      }
    }
  }

  function pickExampleFromMedia(content) {
    if (!content) return null;
    const mt = content["application/json"] || content["application/*+json"];
    if (!mt) return null;
    if (mt.example !== undefined) return mt.example;
    if (mt.examples && Object.values(mt.examples).length > 0) {
      const first = Object.values(mt.examples)[0];
      if (first && first.value !== undefined) return first.value;
    }
    return null;
  }

  function JsonBlock({ title, data }) {
    if (data === null || data === undefined) return null;
    const text =
      typeof data === "string" ? data : JSON.stringify(data, null, 2);
    return (
      <div className="api-portal__example">
        <div className="api-portal__block-title">{title}</div>
        <pre className="api-portal__pre">
          <code>{text}</code>
        </pre>
      </div>
    );
  }

  const reqContent =
    op.requestBody?.content?.["application/json"] ||
    op.requestBody?.content?.["application/*+json"];
  const reqSchema = reqContent?.schema || null;
  const reqExample =
    pickExampleFromMedia(op.requestBody?.content) ??
    (reqSchema ? sampleFromSchema(reqSchema, spec) : null);

  const respSchema =
    op.responses?.["200"]?.content?.["application/json"]?.schema ||
    op.responses?.["201"]?.content?.["application/json"]?.schema ||
    null;
  const respExample =
    pickExampleFromMedia(
      (op.responses?.["200"] || op.responses?.["201"])?.content,
    ) ?? (respSchema ? sampleFromSchema(respSchema, spec) : null);

  return (
    <div className={`api-portal-card ${open ? "is-open" : ""}`}>
      <div className="api-portal-card__row">
        <div className="api-portal-card__left">
          <div
            className="api-portal-card__label"
            style={{ display: "flex", alignItems: "center" }}
          >
            <span>{title}</span>
            {requiresAuth && (
              <img
                src={lockIcon}
                alt="Authorization required"
                title="Authorization required"
                style={{ marginLeft: "8px", width: "14px", height: "14px" }}
              />
            )}
          </div>
          <div className="api-portal-card__path">
            <span
              className={`api-portal__method api-portal__method--${method}`}
            >
              {method.toUpperCase()}
            </span>
            <code className="api-portal__endpoint"> {path}</code>
          </div>
          {subtitle && <div className="api-portal-card__note">{subtitle}</div>}
        </div>

        <div className="api-portal-card__right">
          <button
            type="button"
            className="api-portal__icon-toggle"
            aria-expanded={open ? "true" : "false"}
            aria-controls={`ep-${method}-${path}`}
            onClick={() => setOpen((v) => !v)}
          >
            <span
              className={`icon ${open ? "icon-minus" : "icon-plus"}`}
              aria-hidden="true"
            ></span>
          </button>
        </div>
      </div>

      <div
        id={`ep-${method}-${path}`}
        className="api-portal-card__content"
        hidden={!open}
      >
        {params.length > 0 && (
          <div className="api-portal__block">
            <div className="api-portal__block-title">Parameters</div>
            <ul className="api-portal__params">
              {params.map((p, i) => (
                <li key={i}>
                  <code className="api-portal__code_in">{p.in}</code>
                  <code className="api-portal__code">{p.name}</code>
                  {p.required ? (
                    <span className="api-portal__req">required</span>
                  ) : null}
                  {p.description ? (
                    <span className="api-portal__desc"> — {p.description}</span>
                  ) : null}
                  {p.schema?.type ? (
                    <span className="api-portal__type">{p.schema.type}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {reqSchema && <JsonBlock title="Request body" data={reqExample} />}
        {respSchema && (
          <JsonBlock title="Successful response" data={respExample} />
        )}
      </div>
    </div>
  );
}

export default function ApiPortal() {
  const { isAuthenticated, carrierId } = useContext(AuthContext);
  const [spec, setSpec] = useState(null);
  const [loadingSpec, setLoadingSpec] = useState(false);
  const [error, setError] = useState(null);

  const methodOrder = ["get", "post", "put", "patch", "delete"];

  const carrierLoaded = useMemo(
    () => isAuthenticated && carrierId !== undefined,
    [isAuthenticated, carrierId],
  );
  const hasCarrier = useMemo(
    () => carrierLoaded && carrierId !== null && String(carrierId).length > 0,
    [carrierLoaded, carrierId],
  );

  useEffect(() => {
    if (!hasCarrier) return;
    let abort = false;
    (async () => {
      try {
        setLoadingSpec(true);
        const res = await fetch(OPENAPI_URL);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`OpenAPI fetch failed: ${res.status} ${text}`);
        }
        const txt = await res.text();
        const json = JSON.parse(txt);

        // 2) Wstępny filtr: wytnij oczywiste admin po URL
        const filtered = { ...json, paths: {} };
        for (const [p, ops] of Object.entries(json.paths || {})) {
          const hasAdminSubstr = ADMIN_SUBSTR.some((s) => p.includes(s));
          if (hasAdminSubstr) continue; // /admin, /batch/all, /batch/admin
          filtered.paths[p] = ops;
        }
        // (opcjonalnie) wytnij tag "Admin"
        if (Array.isArray(json.tags)) {
          filtered.tags = json.tags.filter(
            (t) => (t.name || "").toLowerCase() !== "admin",
          );
        }

        if (!abort) setSpec(filtered);
      } catch (e) {
        if (!abort) setError("Failed to load API spec");
      } finally {
        if (!abort) setLoadingSpec(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [hasCarrier]);

  function keyOf(path, method) {
    return `${method.toUpperCase()} ${path}`;
  }
  function isAdminPath(path) {
    if (ADMIN_PREFIXES.some((pref) => path.startsWith(pref))) return true;
    return false;
  }

  // Grupowanie po pierwszym segmencie + finalny filtr deny
  const groups = useMemo(() => {
    if (!spec?.paths) return [];
    const map = new Map();

    for (const [path, operations] of Object.entries(spec.paths)) {
      const segs = path.split("/").filter(Boolean);
      if (segs.length === 0) continue; // brak “Root”

      // Wytnij całe routery adminowe po prefiksie
      if (isAdminPath(path)) continue;

      for (const [method, op] of Object.entries(operations)) {
        if (!["get", "post", "put", "patch", "delete"].includes(method))
          continue;

        // 1) “METHOD PATH” denylista
        const denyKey = keyOf(path, method);
        if (ADMIN_DENY.has(denyKey)) continue;

        // 2) ukryte vendor extensions
        if (isHiddenByExtension(op)) continue;

        // 3) security “admin”
        if (isAdminSec(op)) continue;

        const requiresAuth =
          Array.isArray(op.security) && op.security.length > 0;

        const seg = segs[0];
        const key = seg.toLowerCase();
        const title = seg
          .replace(/[-_]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        if (!map.has(key)) map.set(key, { key, title, items: [] });
        map.get(key).items.push({ path, method, op, requiresAuth });
      }
    }

    const arr = Array.from(map.values()).sort((a, b) =>
      a.title.localeCompare(b.title),
    );
    for (const g of arr) {
      g.items.sort((a, b) => {
        const methodDiff =
          methodOrder.indexOf(a.method) - methodOrder.indexOf(b.method);
        if (methodDiff !== 0) return methodDiff;
        return a.path.localeCompare(b.path);
      });
    }
    return arr;
  }, [spec]);

  return (
    <section className="api-portal">
      <Helmet>
        <title>Developer API | GlobTel</title>
      </Helmet>

      <PageHeader
        title="Developer API"
        description="Explore resources, parameters, and examples to integrate faster."
      />

      <section className="api-portal__wrap api-portal__grid">
        {!carrierLoaded ? (
          <div className="api-portal__placeholder">Loading…</div>
        ) : !hasCarrier ? (
          <div className="api-portal__notice">
            Please allow about 1 business day. We’re verifying your account so
            you can access the Developer API.
          </div>
        ) : error ? (
          <div className="api-portal__notice">{error}</div>
        ) : loadingSpec || !spec ? (
          <div className="api-portal__placeholder">Loading spec…</div>
        ) : (
          <>
            <aside className="api-portal__sidebar" aria-label="Sections">
              <div className="api-portal__toc">
                {groups.map((g) => (
                  <a
                    key={g.key}
                    href={`#${g.key}`}
                    className="api-portal__toc-link"
                  >
                    {g.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="api-portal__content-col">
              {groups.map((g) => (
                <section key={g.key} id={g.key} className="api-portal__section">
                  <div className="api-portal__list">
                    {g.items.map(({ path, method, op, requiresAuth }) => (
                      <EndpointCard
                        key={`${method}:${path}`}
                        path={path}
                        method={method}
                        op={op}
                        spec={spec}
                        requiresAuth={requiresAuth}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </section>
    </section>
  );
}
